/**
 * HOF-626 — inline twin property edit: save through the PUT, show the queued value as
 * "pending" until the refetched row catches up. See the spec §3.4.
 *
 * Pending state is fresh per mount: the server queue is re-read (key outside `['twin', …]`
 * so the SSE sweep never re-triggers the buffer scan), merged ADD-ONLY so a save made while
 * the GET was in flight keeps its newer value — and never for a key saved locally during this
 * mount: the GET's snapshot predates that save, so even after the save was confirmed and its
 * entry cleared, a late-landing GET would otherwise re-insert the obsolete value.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { twinService, type LenseField } from '../services/twinService';
import { pendingEditsKey, twinKeys } from './useTwin';
import { valuesEqual } from '../pages/farm/object/editValue';
import type { PendingEdit } from '../pages/farm/object/PropertyGrid';

export const PENDING_STALE_AFTER_MS = 120_000;
const STALE_CHECK_EVERY_MS = 30_000;

const REFUSAL_KEYS: Record<string, string> = {
    patch_unknown_field: 'farm.edit.errorUnknownField',
    attribute_type_mismatch: 'farm.edit.errorTypeMismatch',
    attribute_not_editable: 'farm.edit.errorNotEditable',
    natural_key_immutable: 'farm.edit.errorNotEditable',
    attribute_value_not_allowed: 'farm.edit.errorNotAllowed',
};

export interface UseInlineEntityEditArgs {
    entityType: string;
    entityId: string;
    enabled: boolean;
    properties: Record<string, unknown> | undefined;
    fields: LenseField[] | undefined;
}

export function useInlineEntityEdit({ entityType, entityId, enabled, properties, fields }: UseInlineEntityEditArgs) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [pending, setPending] = useState<ReadonlyMap<string, PendingEdit>>(() => new Map());
    const [orphanedError, setOrphanedError] = useState<{ key: string; message: string } | null>(null);
    // Keys this mount has saved successfully. The seed is a snapshot that may predate any of
    // them, so it never speaks for these keys again (final review round #3).
    const savedLocally = useRef<Set<string>>(new Set());

    const { data: seed } = useQuery({
        queryKey: pendingEditsKey(entityType, entityId),
        queryFn: () => twinService.getPendingEdits(entityType, entityId),
        enabled,
        staleTime: 0,
        gcTime: 0,
        refetchOnMount: 'always',
        retry: false,
    });

    // Add-only merge: the GET never deletes or overwrites a local entry, nor seeds a key saved here.
    useEffect(() => {
        if (!seed || seed.reason === 'buffer_too_deep' || !seed.pending) return;
        const entries = Object.entries(seed.pending);
        if (entries.length === 0) return;
        setPending((cur) => {
            const next = new Map(cur);
            let changed = false;
            for (const [k, v] of entries) {
                if (!next.has(k) && !savedLocally.current.has(k)) {
                    next.set(k, { value: v, since: Date.now(), stale: false });
                    changed = true;
                }
            }
            return changed ? next : cur;
        });
    }, [seed]);

    const fieldById = useMemo(() => new Map((fields ?? []).map((f) => [f.id, f])), [fields]);

    // Resolve: drop every entry the refetched row has caught up with (schema-aware equality).
    useEffect(() => {
        if (!properties || pending.size === 0) return;
        const caughtUp: string[] = [];
        pending.forEach((entry, key) => {
            if (valuesEqual(fieldById.get(key), properties[key], entry.value)) caughtUp.push(key);
        });
        if (caughtUp.length === 0) return;
        setPending((cur) => {
            const next = new Map(cur);
            caughtUp.forEach((k) => next.delete(k));
            return next;
        });
    }, [properties, fieldById, pending]);

    // Stale: an edit the worker has not applied for two minutes says so.
    const hasFresh = useMemo(() => Array.from(pending.values()).some((e) => !e.stale), [pending]);
    useEffect(() => {
        if (!hasFresh) return undefined;
        const id = setInterval(() => {
            const now = Date.now();
            setPending((cur) => {
                let changed = false;
                const next = new Map(cur);
                cur.forEach((e, k) => {
                    if (!e.stale && now - e.since > PENDING_STALE_AFTER_MS) {
                        next.set(k, { ...e, stale: true });
                        changed = true;
                    }
                });
                return changed ? next : cur;
            });
        }, STALE_CHECK_EVERY_MS);
        return () => clearInterval(id);
    }, [hasFresh]);

    const save = useCallback(
        async (key: string, value: unknown): Promise<void> => {
            let res;
            try {
                res = await twinService.editEntity(entityType, entityId, { [key]: value });
            } catch (err) {
                const refusal = twinService.readEditRefusal(err);
                let message = t('farm.edit.errorServer');
                if (refusal) {
                    const k = REFUSAL_KEYS[refusal.error_kind];
                    if (k) message = t(k);
                    else if (refusal.reason) message = refusal.reason;
                }
                throw new Error(message);
            }
            savedLocally.current.add(key);
            // Only this field's own successful save supersedes its orphaned failure.
            setOrphanedError((cur) => (cur && cur.key === key ? null : cur));
            setPending((cur) => {
                const next = new Map(cur);
                next.set(key, { value: res.field_updates?.[key] ?? value, since: Date.now(), stale: false });
                return next;
            });
            void queryClient.invalidateQueries({ queryKey: twinKeys.entities.all });
        },
        [entityType, entityId, queryClient, t],
    );

    // A save the user walked away from (editor cancelled) that later fails must not vanish.
    const reportOrphanedError = useCallback(
        (key: string, err: unknown) => {
            setOrphanedError({ key, message: err instanceof Error && err.message ? err.message : t('farm.edit.errorServer') });
        },
        [t],
    );
    const dismissOrphanedError = useCallback(() => setOrphanedError(null), []);

    return { pending, save, orphanedError: orphanedError?.message ?? null, reportOrphanedError, dismissOrphanedError };
}
